/* eslint-disable no-unused-vars */
import { getCurrentInstance, inject } from 'vue'
import { appFrontendLogger } from './FrontendLogger'

export const FRONTEND_EXCEPTION_REPORTER_KEY = Symbol('frontend-exception-reporter')

export class FrontendExceptionReporter {
  capture(_error, _context = {}) {}

  child(scope) {
    return new ScopedFrontendExceptionReporter(this, scope)
  }
}

class ScopedFrontendExceptionReporter extends FrontendExceptionReporter {
  constructor(parent, scope) {
    super()
    this.parent = parent
    this.scope = scope
  }

  capture(error, context = {}) {
    const scope = context.scope
      ? `${this.scope}:${context.scope}`
      : this.scope
    this.parent.capture(error, { ...context, scope })
  }
}

function toErrorDetails(error) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'string') {
    return {
      name: 'Error',
      message: error,
      stack: undefined,
    }
  }

  return {
    name: 'NonErrorThrowable',
    message: 'A non-Error value was thrown',
    stack: undefined,
    thrown: error,
  }
}

export function buildFrontendExceptionPayload(error, context = {}, deps = {}) {
  const {
    now = Date.now(),
    href = typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  } = deps

  return {
    timestampMs: now,
    href,
    userAgent,
    ...context,
    error: toErrorDetails(error),
  }
}

export class LoggingFrontendExceptionReporter extends FrontendExceptionReporter {
  constructor({ logger = appFrontendLogger.child('[FrontendException]'), now = () => Date.now(), href = () => (typeof window !== 'undefined' ? window.location.href : undefined), userAgent = () => (typeof navigator !== 'undefined' ? navigator.userAgent : undefined) } = {}) {
    super()
    this.logger = logger
    this.now = now
    this.href = href
    this.userAgent = userAgent
  }

  capture(error, context = {}) {
    const payload = buildFrontendExceptionPayload(error, context, {
      now: this.now(),
      href: this.href(),
      userAgent: this.userAgent(),
    })
    this.logger.error('Captured frontend exception', payload)
  }
}

export const appFrontendExceptionReporter = new LoggingFrontendExceptionReporter()

export function useFrontendExceptionReporter() {
  if (!getCurrentInstance()) {
    return appFrontendExceptionReporter
  }
  return inject(FRONTEND_EXCEPTION_REPORTER_KEY, appFrontendExceptionReporter)
}

export function installGlobalFrontendExceptionHandling({
  app,
  reporter = appFrontendExceptionReporter,
  eventTarget = typeof window !== 'undefined' ? window : null,
  getRoutePath = () => undefined,
} = {}) {
  if (!app?.config) {
    throw new Error('installGlobalFrontendExceptionHandling requires a Vue app with config')
  }

  const previousErrorHandler = app.config.errorHandler
  app.config.errorHandler = (error, instance, info) => {
    reporter.capture(error, {
      source: 'vue-error',
      routePath: getRoutePath(),
      componentName: instance?.$options?.name ?? instance?.type?.name,
      vueInfo: info,
    })
    if (typeof previousErrorHandler === 'function') {
      previousErrorHandler(error, instance, info)
    }
  }

  if (!eventTarget?.addEventListener) {
    return () => {}
  }

  const onWindowError = (event) => {
    reporter.capture(event.error ?? event.message ?? 'Unknown window error', {
      source: 'window-error',
      routePath: getRoutePath(),
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  }

  const onUnhandledRejection = (event) => {
    reporter.capture(event.reason ?? 'Unhandled promise rejection', {
      source: 'unhandled-rejection',
      routePath: getRoutePath(),
    })
  }

  eventTarget.addEventListener('error', onWindowError)
  eventTarget.addEventListener('unhandledrejection', onUnhandledRejection)

  return () => {
    eventTarget.removeEventListener('error', onWindowError)
    eventTarget.removeEventListener('unhandledrejection', onUnhandledRejection)
  }
}
